import { useState } from 'react';
import { Bot, Users, Building2, HeadphonesIcon, Sparkles, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export default function Tools({ userInfo }) {
  const [selectedTool, setSelectedTool] = useState(null);
  const [loading, setLoading] = useState(false);

  const tools = [
    {
      id: 'sales-coach-ai',
      name: 'Sales Coach AI',
      description: 'AI-powered sales coaching and guidance',
      icon: Bot,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      available: true,
      url: 'https://mp3splitter.emergent.host/'
    },
    {
      id: 'upline-ai',
      name: 'Upline AI',
      description: 'Intelligent upline management and insights',
      icon: Users,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      available: false
    },
    {
      id: 'hq-ai',
      name: 'HQ AI',
      description: 'Headquarters intelligence and analytics',
      icon: Building2,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
      available: false
    },
    {
      id: 'support-ai',
      name: 'Support AI',
      description: 'Automated support and assistance',
      icon: HeadphonesIcon,
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600',
      available: false
    }
  ];

  const handleToolClick = (tool) => {
    if (tool.available) {
      setSelectedTool(tool);
      setLoading(true);
    }
  };

  // If a tool is selected, show iframe
  if (selectedTool) {
    return (
      <div className="h-full w-full relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 border-4 border-[#d4af37] border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-neutral-600">Loading {selectedTool.name}...</p>
            </div>
          </div>
        )}
        
        {/* Back button - Bottom Left */}
        <div className="absolute bottom-4 left-4 z-20">
          <Button
            onClick={() => {
              setSelectedTool(null);
              setLoading(false);
            }}
            variant="outline"
            size="sm"
            className="bg-white shadow-lg hover:bg-neutral-50"
          >
            ← Back to Tools
          </Button>
        </div>

        <iframe
          src={selectedTool.url}
          className="w-full h-full border-0"
          title={selectedTool.name}
          onLoad={() => setLoading(false)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  // Show intro page with tool cards
  return (
    <div className="min-h-full w-full bg-gradient-to-br from-neutral-50 to-neutral-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-br from-[#d4af37] to-[#b8941e] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-neutral-900 mb-4">AI-Powered Tools</h1>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            Leverage cutting-edge AI technology to enhance your productivity and streamline your workflow with our suite of intelligent tools.
          </p>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <Card
                key={tool.id}
                className={`group relative overflow-hidden transition-all duration-300 ${
                  tool.available 
                    ? 'hover:shadow-2xl hover:-translate-y-1 cursor-pointer border-2 hover:border-[#d4af37]' 
                    : 'opacity-75 cursor-not-allowed'
                }`}
                onClick={() => handleToolClick(tool)}
              >
                <CardContent className="p-8">
                  {!tool.available && (
                    <div className="absolute top-4 right-4 bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1 rounded-full">
                      Coming Soon
                    </div>
                  )}
                  
                  <div className="flex items-start gap-4">
                    <div className={`w-16 h-16 ${tool.iconBg} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-8 h-8 ${tool.iconColor}`} />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-neutral-900 mb-2 flex items-center gap-2">
                        {tool.name}
                        {tool.available && (
                          <ArrowRight className="w-5 h-5 text-[#d4af37] group-hover:translate-x-1 transition-transform" />
                        )}
                      </h3>
                      <p className="text-neutral-600 text-sm leading-relaxed">
                        {tool.description}
                      </p>
                    </div>
                  </div>

                  {tool.available && (
                    <div className="mt-6 pt-6 border-t border-neutral-100">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-neutral-500 font-medium">Click to launch →</span>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-xs text-green-600 font-semibold">Available Now</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {!tool.available && (
                    <div className="mt-6 pt-6 border-t border-neutral-100">
                      <span className="text-xs text-neutral-400 font-medium">
                        Stay tuned for updates
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center">
          <p className="text-sm text-neutral-500">
            More AI tools are in development. Check back soon for updates!
          </p>
        </div>
      </div>
    </div>
  );
}
